import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

// Constants
const BROCHURE_PATH = '/brochure/avon_bearing_brochure.pdf';
const VERSION_CHECK_PATH = '/brochure/version.json';

export function HeroSection() {
  const isMobile = useIsMobile();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [preloadedBrochure, setPreloadedBrochure] = useState<Blob | null>(null);
  const [brochureVersion, setBrochureVersion] = useState<string>('');

  // Check brochure version
  const checkBrochureVersion = async () => {
    try {
      const response = await fetch(VERSION_CHECK_PATH, {
        method: 'GET',
        cache: 'no-store'
      });
      if (response.ok) {
        const { version } = await response.json();
        setBrochureVersion(version);
        return version;
      }
    } catch (error) {
      console.warn('Failed to check brochure version:', error);
    }
    return null;
  };

  // Preload the brochure when component mounts
  useEffect(() => {
    const preloadBrochure = async () => {
      try {
        const version = await checkBrochureVersion();
        const response = await fetch(`${BROCHURE_PATH}?v=${version}`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          setPreloadedBrochure(blob);
        }
      } catch (error) {
        console.warn('Failed to preload brochure:', error);
      }
    };

    preloadBrochure();
  }, []);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setDownloadError(null);

      // Always check for latest version before download
      const version = await checkBrochureVersion();
      let blob: Blob;

      if (preloadedBrochure && version === brochureVersion) {
        blob = preloadedBrochure;
      } else {
        const response = await fetch(`${BROCHURE_PATH}?v=${version}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
        }

        blob = await response.blob();
        setPreloadedBrochure(blob);
        setBrochureVersion(version || '');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'avon_bearings_brochure.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError('Failed to download the brochure. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section
      className={cn(
        "relative flex items-center overflow-hidden bg-background",
        isMobile
          ? "min-h-[100svh] py-20"
          : "min-h-screen py-24 md:py-32"
      )}
    >
      {/* Modern Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>

      {/* Radial Gradient overlay for visual richness */}
      <div className="absolute left-0 right-0 top-0 bottom-0 bg-[radial-gradient(circle_at_center,transparent_30%,hsl(var(--background))_100%)] pointer-events-none"></div>

      {/* Decorative Glow Elements */}
      <div className="absolute left-1/4 top-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slow"></div>
      <div className="absolute right-1/4 bottom-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slow"></div>

      <div className="container relative z-10 px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
          
          {/* Left Column: Copy & Actions */}
          <div className="lg:col-span-7 flex flex-col items-center text-center lg:items-start lg:text-left max-w-2xl mx-auto lg:mx-0 lg:max-w-none">
            
            {/* Top Pill Badge */}
            <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs sm:text-sm font-semibold text-primary mb-6 animate-fade-in shadow-[0_0_15px_rgba(255,165,0,0.1)]">
              <span className="flex h-2 w-2 rounded-full bg-primary mr-2 animate-pulse"></span>
              Authorized SKF, FAG, NBC & Timken Dealer
            </div>

            {/* Brand Category Tag */}
            <span className="text-xs sm:text-sm font-bold tracking-widest text-primary uppercase mb-2 animate-fade-in">
              AVON BEARINGS
            </span>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl xl:text-7.5xl font-black tracking-tight leading-[1.05] text-foreground mb-6 animate-fade-in">
              High-Precision <br />
              <span className="avon-gradient-text">Industrial Bearings</span>
              {/* Visually Hidden Tagline for SEO Search Crawler Indexing */}
              <span className="sr-only"> - Best Bearing Shop and Authorized Dealer in Wankaner & Morbi (SKF, FAG, NBC, Timken)</span>
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground/95 mb-8 font-medium leading-relaxed animate-fade-in-delay-1 max-w-xl mx-auto lg:mx-0">
              Authorized distributor delivering 100% genuine industrial bearings, linear motion guides, and mechanical components. Morbi and Wankaner's premier choice for engineering reliability.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 w-full sm:w-auto mb-12 animate-fade-in-delay-2">
              <Button
                onClick={handleDownload}
                size="lg"
                className="w-full sm:w-auto text-sm sm:text-base h-14 px-8 rounded-full shadow-[0_0_20px_rgba(255,165,0,0.3)] hover:shadow-[0_0_30px_rgba(255,165,0,0.5)] transition-all duration-300 group"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Downloading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Download Brochure
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 group-hover:-translate-y-1 transition-transform"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
                  </span>
                )}
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-sm sm:text-base h-14 px-8 rounded-full border-2 hover:bg-muted/50 transition-all duration-300 group"
                asChild
              >
                <a href="#categories">
                  Explore Products
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 group-hover:translate-x-1 transition-transform"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                </a>
              </Button>
            </div>

            {downloadError && (
              <p className="mb-6 text-red-500 font-medium bg-red-500/10 px-4 py-2 rounded-lg animate-shake inline-block">{downloadError}</p>
            )}

            {/* B2B Trust Metrics / Stats Container */}
            <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-10 text-center lg:text-left w-full border-t border-border/60 pt-8 animate-fade-in-delay-3">
              <div className="flex flex-col items-center lg:items-start">
                <span className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">100%</span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-bold mt-1 uppercase tracking-wider">Genuine Brands</span>
              </div>
              <div className="flex flex-col border-x border-border/40 px-2 sm:px-4 md:px-8 items-center lg:items-start">
                <span className="text-2xl sm:text-3xl font-extrabold avon-gradient-text tracking-tight">25+</span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-bold mt-1 uppercase tracking-wider">Years of Trust</span>
              </div>
              <div className="flex flex-col items-center lg:items-start">
                <span className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">10k+</span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-bold mt-1 uppercase tracking-wider">Stock Ready</span>
              </div>
            </div>

          </div>

          {/* Right Column: Interactive CSS Bearing Graphic */}
          <div className="lg:col-span-5 flex items-center justify-center relative w-full min-h-[350px] sm:min-h-[400px] md:min-h-[450px] animate-fade-in-delay-1 select-none">
            
            {/* Ambient Background Radial Glow */}
            <div className="absolute w-[80%] h-[80%] bg-gradient-to-tr from-primary/30 to-orange-500/10 rounded-full blur-[80px] opacity-40 animate-pulse"></div>
            
            {/* High-Tech HUD Rings */}
            <div className="absolute w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] rounded-full border border-primary/20 animate-[spin_50s_linear_infinite]"></div>
            <div className="absolute w-[300px] h-[300px] sm:w-[380px] sm:h-[380px] rounded-full border border-dashed border-border/40 animate-[spin_90s_linear_infinite_reverse]"></div>

            {/* Bearing Outer Ring (Stationary outer race) */}
            <div className="absolute w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] rounded-full border-[10px] border-slate-300 dark:border-zinc-700 shadow-[0_0_30px_rgba(0,0,0,0.15)] dark:shadow-[0_0_30px_rgba(255,165,0,0.08)] flex items-center justify-center">
              
              {/* Inner depth groove */}
              <div className="w-full h-full rounded-full border-4 border-slate-400/30 dark:border-zinc-600/50"></div>
            </div>

            {/* Bearing Balls Cage (Slow clockwise rotation) */}
            <div className="absolute w-[210px] h-[210px] sm:w-[260px] sm:h-[260px] animate-[spin_35s_linear_infinite]">
              {/* Steel Ball 1 */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 2 */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 3 */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 4 */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 5 */}
              <div className="absolute top-[14.6%] left-[14.6%] -translate-x-[30%] -translate-y-[30%] w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 6 */}
              <div className="absolute top-[14.6%] right-[14.6%] translate-x-[30%] -translate-y-[30%] w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 7 */}
              <div className="absolute bottom-[14.6%] left-[14.6%] -translate-x-[30%] translate-y-[30%] w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
              {/* Steel Ball 8 */}
              <div className="absolute bottom-[14.6%] right-[14.6%] translate-x-[30%] translate-y-[30%] w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-400 border border-white/60 shadow-[0_4px_10px_rgba(0,0,0,0.25)] dark:from-zinc-400 dark:via-zinc-200 dark:to-zinc-600"></div>
            </div>

            {/* Bearing Inner Ring (sits statically as a frame) */}
            <div className="absolute w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] rounded-full border-[8px] border-slate-300 dark:border-zinc-700 bg-background flex items-center justify-center shadow-[0_0_20px_rgba(0,0,0,0.1)] dark:shadow-[0_0_20px_rgba(255,165,0,0.05)]">
              
              {/* Inner ring core with high-precision details (Spins) */}
              <div className="absolute w-[85%] h-[85%] rounded-full border-2 border-dashed border-primary/40 animate-[spin_20s_linear_infinite_reverse]"></div>
              
              {/* Center Core Hub (STABLE - sits on top of spinning elements) */}
              <div className="absolute w-[58%] h-[58%] rounded-full bg-gradient-to-tr from-slate-200 to-white dark:from-zinc-900 dark:to-zinc-800 shadow-[inset_0_2px_5px_rgba(0,0,0,0.15)] flex flex-col items-center justify-center border border-border z-10">
                {/* Decorative High-Tech Hub Details */}
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary animate-pulse" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span className="text-[7px] sm:text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">AVON</span>
              </div>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
